import { updateDocument, createDocument, getId } from 'services/firestore'

import { updateParentHasOneRelationship } from 'hooks'
import { Database } from 'models'
import { message } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import { useState } from 'react'
import { useTranslations } from 'contexts/Translation'

const useActionsDatabaseAdvancedForm = ({ initialData, form } = {}) => {
  // [COMPONENT_STATE_HOOKS]
  const [loading, setLoading] = useState(false)

  // [ADDITIONAL_HOOKS]
  const { t } = useTranslations()
  const history = useHistory()
  const params = useParams()

  const prepareValues = async (values = {}, additionalValues = {}) => {
    const databaseId = initialData?.['database']?._id || getId('databases')
    const additionalReferences = { databaseId }
    const preparedValues = {
      _id: databaseId,
      ...additionalValues,
      dataModels: values?.['database']?.['dataModels'] ?? null,
      enums: values?.['database']?.['enums'] ?? null
    }
    if (params.customerId) preparedValues['customerId'] = params.customerId
    return preparedValues
  }

  const saveForm = async (values, callback) => {
    try {
      // Prepare data to be saved
      const data = await prepareValues(values)
      // Updating parent references
      if (params.customerId)
        await updateParentHasOneRelationship(
          params.customerId,
          'customers',
          'databaseId',
          data._id
        )
      // Save data
      if (initialData) {
        await updateDocument('databases', initialData.database?._id, data)
        message.success(t('Database successfully updated'))
      } else {
        await createDocument('databases', data, data._id)
        message.success(t('Database successfully created'))
      }
      // Final callback
      callback?.()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Something went wrong during data save'))
    }
  }

  const validateForm = (values) => {
    try {
      // Prepare data to be validated
      const validationData = {
        dataModels: values?.['database']?.['dataModels'],
        enums: values?.['database']?.['enums']
      }
      Database.validationSchema.validateSync(validationData)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Database validation error: ') + t(error.message))
    }
  }

  const onFinish = async () => {
    if (loading) return // Avoid multiple calls

    try {
      setLoading(true)
      // Get form values
      const formValues = form.getFieldsValue()
      // Validate fields
      validateForm(formValues)
      // Final callback
      const callback = () => history.goBack()
      // Save data
      await saveForm(formValues, callback)
    } catch (error) {
      setLoading(false)
      message.error(error.message)
    }
  }

  const onReset = () => {
    form.resetFields()
    history.goBack()
  }

  return { onFinish, onReset, loading, saveForm, validateForm, prepareValues }
}

export default useActionsDatabaseAdvancedForm
